import { Analytics } from '@cobliteam/analytics';
import { BlueprintProvider as BlueprintProviderLib, type ExportConfig } from '@cobliteam/blueprint-react-renderer';
import { useLanguage } from '@cobliteam/i18n';
import Environment from '@cobliteam/react-environments';
import ButtonMediaDetails from '@cobliteam/ui-business/components/Button/ButtonMediaDetails';
import DialogMedia from '@cobliteam/ui-business/components/dialogs/DialogMedia';
import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { routeRegistry } from './config/navigation';
import BlueprintRequester from './config/requester';
import useOnNavigate from './hooks/useOnNavigate';
import useExportRequestStore from './store/useExportRequestStore';
import useNavigationHistoryStore from './store/useNavigationHistoryStore';
import useOverlayMediaStore from './store/useOverlayMediaStore';

interface BlueprintProviderProps {
  children: React.ReactNode;
}

const env = Environment.getInstance().getEnvironment();

const BlueprintProvider = ({ children }: BlueprintProviderProps) => {
  const requester = useMemo(() => new BlueprintRequester({ host: env.blueprintApiHost }), []);
  const { currentLanguage: language } = useLanguage();
  const overlayMediaId = useOverlayMediaStore((s) => s.mediaId);
  const clearOverlay = useOverlayMediaStore((s) => s.clear);
  const onNavigate = useOnNavigate();
  const openExportRequest = useExportRequestStore((s) => s.open);
  const history = useHistory();
  const screen = {
    width: typeof window === 'undefined' ? 1920 : window.innerWidth,
    height: typeof window === 'undefined' ? 1080 : window.innerHeight,
  };

  const onTrackEvent = useCallback((eventName: string, properties?: Record<string, unknown>) => Analytics.track(eventName, { ...properties, source: 'blueprint' }), []);

  const onExport = useCallback(
    async (config: ExportConfig) => {
      openExportRequest(config);
    },
    [openExportRequest]
  );

  const onNavigateBack = useCallback(
    (fallbackPath: string, fallbackParams?: Record<string, unknown>) => {
      if (useNavigationHistoryStore.getState().hasNavigatedInApp) {
        history.goBack();
        return;
      }
      const searchParams = new URLSearchParams();
      if (fallbackParams) {
        for (const [key, value] of Object.entries(fallbackParams)) {
          if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        }
      }
      const qs = searchParams.toString();
      history.replace(qs ? `${fallbackPath}?${qs}` : fallbackPath);
    },
    [history]
  );

  return (
    <BlueprintProviderLib
      config={{
        navigation: {
          routeRegistry,
          onNavigate,
          onNavigateBack,
        },
        requester,
        language,
        screen,
        onTrackEvent,
        onExport,
      }}
    >
      {children}
      {overlayMediaId && <DialogMedia mediaId={overlayMediaId} origin='Triggers' onClose={clearOverlay} redirectButton={<ButtonMediaDetails mediaId={overlayMediaId} />} />}
    </BlueprintProviderLib>
  );
};

export { BlueprintProvider };
